import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import {
  capturePanelFailure,
  panelLoadBreadcrumb,
} from "../../config/capturePanelFailure";
import { buildStaffTimeline } from "./buildStaffTimeline";
import ConsentStatusBadge from "../consent/ConsentStatusBadge";
import {
  bindPontosenseDevice,
  fetchPontosenseCareWebLauncher,
  fetchPontosenseDevices,
  fetchPontosenseIntegrationEnabled,
  fetchPontosenseValidationEvents,
  setPontosenseIntegrationEnabled,
  unbindPontosenseDevice,
} from "../../api/pontosenseService";
import {
  formatClientZonedSessionEnd,
  formatClientZonedTime,
} from "../../utils/activityTimezone";

// GMA-1566: render an occupancy session's length in plain words.
const formatDuration = (ms) => {
  if (!Number.isFinite(ms) || ms < 0) return "";
  const totalMin = Math.round(ms / 60000);
  if (totalMin < 1) return "<1 min";
  if (totalMin < 60) return `${totalMin} min`;
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return m ? `${h} hr ${m} min` : `${h} hr`;
};


/**
 * GMA-1148, GMA-1293, GMA-1295, GMA-1402 — Pontosense staff panel.
 * Admin: Xandar-style integration toggle + bind. Caregiver: read-only pane.
 */
const PontosenseStaffSection = ({
  clientId,
  // cortex-be#1501 / GMA-1402: default OFF until the authoritative read says on.
  integrationEnabled = false,
  canManageIntegration = false,
  onIntegrationChanged,
  // #494 — these rows are the CLIENT's events, so they belong on the
  // client's clock. Absent or unparseable falls back to the caregiver
  // default inside the helper, never to the viewer's zone.
  clientTimezone,
}) => {
  const [events, setEvents] = useState([]);
  // GMA-1504 + GMA-1566: build the staff timeline — occupancy/bed/couch stays
  // collapse into one session row with a start, end, and duration; every other
  // event keeps the same-window grouping so simultaneous events read as one
  // moment instead of contradictory same-second lines.
  const timeline = useMemo(() => buildStaffTimeline(events), [events]);
  const [sensorSn, setSensorSn] = useState("");
  const [enabled, setEnabled] = useState(integrationEnabled);
  const [status, setStatus] = useState("");
  const [statusTone, setStatusTone] = useState("info");
  const [loading, setLoading] = useState(false);
  const [toggling, setToggling] = useState(false);
  // GMA-1865: block toggle until authoritative status GET settles.
  const [statusSettled, setStatusSettled] = useState(false);
  // GMA-1441: dedicated in-progress flag for the assign action so the button
  // can show "Assigning…" only during a real bind, not during the mount-time
  // events refresh (which also flips `loading`).
  const [binding, setBinding] = useState(false);
  // GMA-1557: assigned-sensor list + per-sensor remove (admin only).
  const [devices, setDevices] = useState([]);
  const [removingSn, setRemovingSn] = useState(null);
  // #459: ignore late responses after a client switch on the profile page.
  const isMounted = useRef(true);
  const activeClientId = useRef(clientId);
  const statusGen = useRef(0);
  const toggleStarted = useRef(false);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const setPanelStatus = (message, tone = "info") => {
    setStatus(message);
    setStatusTone(tone);
  };

  // Review finding, partially adopted. The four ACTION catches below lacked the
  // stale/unmount guard the two loaders carry. The review asked for the guard
  // around the whole catch; that is right for a LOAD and wrong for an ACTION.
  //
  // A load failure on an unmounted panel means nobody was shown anything, so
  // reporting it is noise. An action failure is real whether or not the
  // caregiver is still looking — the sensor genuinely was not assigned, and
  // that is exactly the kind of failure this panel exists to surface. So the
  // capture stays unguarded and only the UI write is guarded: a toast reading
  // "Assign sensor failed" rendered over a DIFFERENT client's page is worse
  // than silence, because it attributes the failure to the wrong client.
  const showPanelError = (message) => {
    if (!isMounted.current || activeClientId.current !== clientId) return;
    setPanelStatus(message, "error");
    toast.error(message);
  };

  useEffect(() => {
    activeClientId.current = clientId;
    setEnabled(integrationEnabled);
    setStatusSettled(false);
    toggleStarted.current = false;
    setEvents([]);
    setDevices([]);
    setSensorSn("");
    setStatus("");
    setStatusTone("info");
    setLoading(false);
    if (!clientId) {
      setStatusSettled(true);
      return undefined;
    }
    const gen = ++statusGen.current;
    let cancelled = false;
    fetchPontosenseIntegrationEnabled(clientId)
      .then((on) => {
        if (
          cancelled ||
          activeClientId.current !== clientId ||
          gen !== statusGen.current ||
          toggleStarted.current
        ) {
          return;
        }
        setEnabled(on);
        setStatusSettled(true);
      })
      .catch(() => {
        // Keep the roster hint on read failure.
        if (
          cancelled ||
          activeClientId.current !== clientId ||
          gen !== statusGen.current ||
          toggleStarted.current
        ) {
          return;
        }
        setStatusSettled(true);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- GMA-1865
  }, [clientId]);

  const refresh = useCallback(async () => {
    if (!clientId) return;
    const requestClientId = clientId;
    setLoading(true);
    try {
      const data = await fetchPontosenseValidationEvents(clientId);
      if (
        !isMounted.current ||
        activeClientId.current !== requestClientId
      ) {
        return;
      }
      setEvents(data?.events || []);
      panelLoadBreadcrumb("pontosense", true, {
        clientId,
        rowCount: (data?.events || []).length,
      });
    } catch (err) {
      if (
        !isMounted.current ||
        activeClientId.current !== requestClientId
      ) {
        return;
      }
      capturePanelFailure(err, {
        panel: "pontosense-staff",
        surfaceKey: "pontosense",
        op: "load-events",
        clientId,
      });
      setPanelStatus(err.message || "Failed to load Pontosense events", "error");
    } finally {
      if (
        isMounted.current &&
        activeClientId.current === requestClientId
      ) {
        setLoading(false);
      }
    }
  }, [clientId]);

  // GMA-1557: load the client's bound sensors so an admin can see and remove
  // them. Admin-only (matches the bind/remove controls); non-fatal on failure
  // so the events pane still renders.
  const refreshDevices = useCallback(async () => {
    if (!clientId || !canManageIntegration) return;
    const requestClientId = clientId;
    try {
      const data = await fetchPontosenseDevices(clientId);
      if (
        !isMounted.current ||
        activeClientId.current !== requestClientId
      ) {
        return;
      }
      setDevices(data?.devices || []);
    } catch (err) {
      if (
        !isMounted.current ||
        activeClientId.current !== requestClientId
      ) {
        return;
      }
      capturePanelFailure(err, {
        panel: "pontosense-staff",
        surfaceKey: "pontosense",
        op: "load-devices",
        clientId,
      });
      setPanelStatus(err.message || "Failed to load assigned sensors", "error");
    }
  }, [clientId, canManageIntegration]);

  useEffect(() => {
    refresh();
    refreshDevices();
  }, [refresh, refreshDevices]);

  const handleBind = async () => {
    if (loading || toggling || binding) return;
    // GMA-1317 (QA): with the integration disabled, every action in this
    // section must be inert. The button is disabled too — this guard covers
    // keyboard/programmatic activation.
    if (!enabled) {
      setPanelStatus(
        "Pontosense is disabled for this client. Enable it to assign a sensor.",
        "error",
      );
      return;
    }
    // Capture the trimmed serial once, before any await, so a re-render that
    // clears the input (or a fast second click) cannot change what we report
    // in the success message (Copilot review, PR #207).
    const boundSn = sensorSn.trim();
    // GMA-1441: an empty or whitespace-only serial used to silently do nothing
    // when Assign was clicked, which read as a broken button. Tell the user
    // what to do instead of failing silently.
    if (!boundSn) {
      const message = "Enter a Silver Shield serial number to assign.";
      setPanelStatus(message, "error");
      toast.error(message);
      return;
    }
    setBinding(true);
    try {
      await bindPontosenseDevice(clientId, { sensorSn: boundSn });
      setSensorSn("");
      await refresh();
      await refreshDevices();
      const successMessage = `Sensor ${boundSn} is now assigned to this client. Fall and room alerts will appear here after the sensor sends its next event to CompassCare.`;
      setPanelStatus(successMessage, "success");
      toast.success(successMessage);
    } catch (err) {
      capturePanelFailure(err, {
        panel: "pontosense-staff",
        surfaceKey: "pontosense",
        op: "assign-sensor",
        clientId,
      });
      showPanelError(err.message || "Assign sensor failed");
    } finally {
      setBinding(false);
    }
  };

  // GMA-1557: remove (soft-unbind) a sensor once assigned. Admin-only and inert
  // while the integration is disabled (GMA-1317), mirroring the assign action.
  // Confirms first because it stops alert routing for that sensor.
  const handleRemove = async (sn) => {
    if (loading || toggling || binding || removingSn) return;
    if (!enabled) {
      setPanelStatus(
        "Pontosense is disabled for this client. Enable it to manage the sensor.",
        "error",
      );
      return;
    }
    if (!sn) return;
    if (
      typeof window !== "undefined" &&
      typeof window.confirm === "function" &&
      !window.confirm(
        `Remove sensor ${sn} from this client? Its fall and room alerts will stop routing to caregivers.`,
      )
    ) {
      return;
    }
    setRemovingSn(sn);
    try {
      await unbindPontosenseDevice(clientId, sn);
      await refreshDevices();
      await refresh();
      const message = `Sensor ${sn} removed from this client.`;
      setPanelStatus(message, "success");
      toast.success(message);
    } catch (err) {
      capturePanelFailure(err, {
        panel: "pontosense-staff",
        surfaceKey: "pontosense",
        op: "remove-sensor",
        clientId,
      });
      showPanelError(err.message || "Remove sensor failed");
    } finally {
      setRemovingSn(null);
    }
  };

  const handleToggle = async () => {
    // GMA-1441 (Copilot review): also block while a bind is in flight so a
    // toggle cannot race the bind/refresh and overwrite its status updates.
    if (toggling || !statusSettled || loading || binding) return;
    const next = !enabled;
    toggleStarted.current = true;
    setToggling(true);
    try {
      await setPontosenseIntegrationEnabled(clientId, next);
      setEnabled(next);
      setPanelStatus(
        next
          ? "Pontosense integration enabled for this client."
          : "Pontosense integration disabled (events still logged).",
        "info",
      );
      // onIntegrationChanged may be async (ClientProfilePage awaits a client
      // refetch). Await it in its own guard so a refetch rejection can't become
      // an unhandled promise rejection or mask the toggle-success status
      // (Copilot review, PR #192).
      try {
        await onIntegrationChanged?.(next);
      } catch (cbErr) {
        console.warn("Pontosense onIntegrationChanged callback failed", cbErr);
      }
    } catch (err) {
      capturePanelFailure(err, {
        panel: "pontosense-staff",
        surfaceKey: "pontosense",
        op: "toggle-integration",
        clientId,
      });
      showPanelError(err.message || "Toggle failed");
    } finally {
      setToggling(false);
    }
  };

  const openCareWeb = async () => {
    // GMA-1317 (QA): no Pontosense actions while the integration is disabled.
    // The button is disabled too — this guard covers keyboard/programmatic
    // activation so Care Web can never be launched for a disabled client.
    if (!enabled) {
      setPanelStatus(
        "Pontosense is disabled for this client. Enable it to manage the sensor.",
        "error",
      );
      return;
    }
    try {
      const data = await fetchPontosenseCareWebLauncher(clientId);
      if (data?.url) {
        window.open(data.url, "_blank", "noopener,noreferrer");
      }
    } catch (err) {
      capturePanelFailure(err, {
        panel: "pontosense-staff",
        surfaceKey: "pontosense",
        op: "careweb-launcher",
        clientId,
      });
      showPanelError(err.message || "Launcher failed");
    }
  };

  const statusColor =
    statusTone === "success"
      ? "#1B5E20"
      : statusTone === "error"
        ? "#B71C1C"
        : "#1565C0";
  const statusBackground =
    statusTone === "success"
      ? "#E8F5E9"
      : statusTone === "error"
        ? "#FFEBEE"
        : "transparent";

  const panelStyle = {
    marginBottom: "24px",
    backgroundColor: enabled ? "#E8F5E9" : "#D9D9D9",
    color: "#333",
    padding: "16px",
    borderRadius: "8px",
    border: enabled ? "1px solid #C8E6C9" : "1px solid #BDBDBD",
    borderLeft: enabled ? "4px solid #4CAF50" : "4px solid #9E9E9E",
  };

  // GMA-1557: only sensors still bound (not soft-removed) are offered for removal.
  const activeDevices = devices.filter((d) => d?.status !== "inactive");

  return (
    <div className="client-profile-section" style={panelStyle}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "12px",
        }}
      >
        <h2 style={{ fontSize: "1.1rem", margin: 0, fontWeight: 800 }}>
          Pontosense Silver Shield
        </h2>
        {canManageIntegration ? (
          <label
            style={{
              display: "flex",
              alignItems: "center",
              cursor: toggling || !statusSettled ? "not-allowed" : "pointer",
              gap: "8px",
            }}
          >
            <span style={{ fontSize: "0.9rem", color: "#555" }}>
              {enabled ? "On for caregivers" : "Off for caregivers"}
            </span>
            <div
              role="switch"
              aria-checked={enabled}
              aria-disabled={toggling || !statusSettled}
              aria-label="Pontosense integration"
              tabIndex={toggling || !statusSettled ? -1 : 0}
              onClick={toggling || !statusSettled ? undefined : handleToggle}
              onKeyDown={(e) => {
                if (
                  !toggling &&
                  statusSettled &&
                  (e.key === "Enter" || e.key === " " || e.key === "Spacebar")
                ) {
                  e.preventDefault();
                  handleToggle();
                }
              }}
              style={{
                width: "48px",
                height: "26px",
                borderRadius: "13px",
                backgroundColor: enabled ? "#4CAF50" : "#BDBDBD",
                position: "relative",
                transition: "background-color 0.3s ease",
                opacity: toggling || !statusSettled ? 0.6 : 1,
              }}
            >
              <div
                style={{
                  width: "22px",
                  height: "22px",
                  borderRadius: "50%",
                  backgroundColor: "#fff",
                  position: "absolute",
                  top: "2px",
                  left: enabled ? "24px" : "2px",
                  transition: "left 0.3s ease",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
                }}
              />
            </div>
          </label>
        ) : (
          <span style={{ fontSize: "0.9rem", color: "#555" }}>
            {enabled ? "Enabled" : "Disabled"}
          </span>
        )}
      </div>

      <p style={{ fontSize: "0.9rem", color: "#555", marginTop: 0 }}>
        Staff validation pane (recent sensor events). Pair the exact serial from
        the Silver Shield label, then open Pontosense Care Web for live device state.
      </p>

      <ConsentStatusBadge
        clientId={clientId}
        sensorType="pontosense"
        enabled={enabled}
      />

      {!enabled && (
        <p style={{ fontSize: "0.9rem", color: "#666", marginBottom: "12px" }}>
          {canManageIntegration
            ? "Enable Pontosense for this client to route caregiver alerts. Events may still appear below for audit."
            : "Pontosense is not enabled for this client. Ask a caregiver admin to turn it on."}
        </p>
      )}

      <div
        style={{
          display: "flex",
          gap: "8px",
          marginBottom: "12px",
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        {canManageIntegration && (
          <>
            <label
              htmlFor="pontosense-sensor-sn"
              style={{
                position: "absolute",
                left: "-9999px",
                width: "1px",
                height: "1px",
                overflow: "hidden",
              }}
            >
              Silver Shield serial number
            </label>
            <input
              id="pontosense-sensor-sn"
              name="pontosenseSensorSn"
              type="text"
              aria-label="Silver Shield serial number"
              placeholder="Serial Number (Inside the box)"
              value={sensorSn}
              onChange={(e) => setSensorSn(e.target.value)}
              style={{ flex: 1, minWidth: "200px", padding: "8px" }}
              disabled={!enabled}
            />
            <button
              type="button"
              onClick={handleBind}
              disabled={!enabled || loading || toggling || binding}
            >
              {binding ? "Assigning…" : "Assign sensor"}
            </button>
          </>
        )}
        {/* GMA-1317 (QA): every control in this section is inert while the
            integration is disabled — only the admin toggle stays usable so
            the integration can be turned back on. */}
        <button type="button" onClick={openCareWeb} disabled={!enabled}>
          Manage in Pontosense
        </button>
        <button
          type="button"
          onClick={refresh}
          disabled={!enabled || loading || toggling || binding}
        >
          Refresh
        </button>
      </div>

      {/* GMA-1557: assigned sensors + per-sensor remove (admin only). */}
      {canManageIntegration && (
        <div style={{ marginBottom: "12px" }}>
          <h3 style={{ fontSize: "0.95rem", margin: "0 0 6px", fontWeight: 700 }}>
            Assigned sensors
          </h3>
          {activeDevices.length === 0 ? (
            <p style={{ fontSize: "0.85rem", color: "#666", margin: 0 }}>
              No sensors assigned to this client yet.
            </p>
          ) : (
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {activeDevices.map((d) => (
                <li
                  key={d.sensorSn}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    padding: "4px 0",
                  }}
                >
                  <span style={{ fontFamily: "monospace", fontSize: "0.85rem" }}>
                    {d.sensorSn}
                  </span>
                  {d.pontosenseRoomId ? (
                    <span style={{ fontSize: "0.8rem", color: "#666" }}>
                      room {d.pontosenseRoomId}
                    </span>
                  ) : null}
                  <button
                    type="button"
                    aria-label={`Remove sensor ${d.sensorSn}`}
                    onClick={() => handleRemove(d.sensorSn)}
                    disabled={
                      !enabled ||
                      loading ||
                      toggling ||
                      binding ||
                      removingSn !== null
                    }
                    style={{ marginLeft: "auto" }}
                  >
                    {removingSn === d.sensorSn ? "Removing…" : "Remove"}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {status ? (
        <p
          style={{
            fontSize: statusTone === "success" ? "0.95rem" : "0.85rem",
            color: statusColor,
            backgroundColor: statusBackground,
            padding: statusTone === "success" ? "10px 12px" : 0,
            borderRadius: statusTone === "success" ? "6px" : 0,
            fontWeight: statusTone === "success" ? 600 : 400,
            border:
              statusTone === "success" ? "1px solid #A5D6A7" : "none",
          }}
          role={statusTone === "error" ? "alert" : "status"}
          aria-live={statusTone === "error" ? "assertive" : "polite"}
        >
          {status}
        </p>
      ) : null}

      {loading && events.length === 0 ? (
        <p>Loading events…</p>
      ) : events.length === 0 ? (
        <p>No Pontosense events yet for this client.</p>
      ) : (
        <table style={{ width: "100%", fontSize: "0.85rem" }}>
          <thead>
            <tr>
              <th align="left">Time</th>
              <th align="left">Activity</th>
              <th align="left">Duration</th>
              <th align="left">Sensor</th>
            </tr>
          </thead>
          <tbody>
            {/* GMA-1566: occupancy stays render as one session row (start – end,
                duration); other events keep the GMA-1504 grouped row. */}
            {timeline.slice(0, 10).map((entry) =>
              entry.kind === "session" ? (
                <tr key={`session-${entry.events[0]?.id}`}>
                  <td>
                    {formatClientZonedTime(entry.startAt, clientTimezone)}
                    {" – "}
                    {entry.ongoing
                      ? "now"
                      : formatClientZonedSessionEnd(
                          entry.endAt,
                          entry.startAt,
                          clientTimezone,
                        )}
                  </td>
                  <td>{entry.label}</td>
                  <td>
                    {entry.ongoing
                      ? "ongoing"
                      : formatDuration(entry.durationMs)}
                  </td>
                  <td>{entry.sensorSn || "—"}</td>
                </tr>
              ) : (
                <tr key={entry.key}>
                  <td>{formatClientZonedTime(entry.eventAt, clientTimezone)}</td>
                  <td>{entry.combinedLabel}</td>
                  <td>—</td>
                  <td>{entry.sensorSn || "—"}</td>
                </tr>
              ),
            )}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default PontosenseStaffSection;
